import { useFileBlob, useUploadFile } from '@/hooks/useFile'
import type { FileDto } from '@/types/file'
import { useCallback, useEffect, useState } from 'react'

type UseFileUploadFieldProps = {
  value?: string[]
  onChange: (ids: string[]) => void
  onUploadError?: (error: unknown) => void
  fetchFile?: boolean
  maxFiles?: number
}

export function useFileUploadField({
  value = [],
  onChange,
  onUploadError,
  fetchFile = false,
  maxFiles = 1,
}: UseFileUploadFieldProps) {
  const uploadFile = useUploadFile()
  const [files, setFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadedFiles, setUploadedFiles] = useState<FileDto[]>([])
  const firstFileId = value[0] || undefined
  const valueKey = value.join('|')
  const {
    data: fileBlob,
    isLoading: isLoadingFile,
    isFetching: isFetchingFile,
    refetch: refetchFile,
  } = useFileBlob(firstFileId, fetchFile)

  useEffect(() => {
    if (value.length > 0) return

    setFiles([])
    setUploadedFiles([])
    setUploadProgress(0)
  }, [valueKey])

  const handleChange = useCallback(
    async (nextFiles: File[]) => {
      const filesToUpload = nextFiles.slice(0, maxFiles)
      setFiles(filesToUpload)
      setUploadProgress(0)

      if (filesToUpload.length === 0) {
        setUploadedFiles([])
        onChange([])
        return
      }

      try {
        const uploaded: FileDto[] = []

        for (const file of filesToUpload) {
          const res = await uploadFile.mutateAsync({
            file,
            onProgress: setUploadProgress,
          })
          uploaded.push(res)
        }

        const nextUploaded = [
          ...uploadedFiles,
          ...uploaded,
        ].slice(0, maxFiles)

        setUploadedFiles(nextUploaded)

        onChange([
          ...value,
          ...uploaded.map(file => file.id),
        ].slice(0, maxFiles))

        setFiles(prev =>
          prev.filter(
            file =>
              !filesToUpload.some(
                f =>
                  f.name === file.name &&
                  f.size === file.size &&
                  f.lastModified === file.lastModified
              )
          )
        )
        // setUploadedFiles(uploaded)
        // onChange(uploaded.map(file => file.id))
      } catch (error) {
        setFiles([])
        setUploadedFiles([])
        setUploadProgress(0)
        onChange([])
        onUploadError?.(error)
      }
    },
    [maxFiles, onChange, onUploadError, uploadFile],
  )

  const handleRemoveExisting = useCallback(
    (index: number) => {
      const nextIds = value.filter((_, i) => i !== index)

      setUploadProgress(0)

      onChange(nextIds)
    },
    [onChange, value]
  )

  // const handleRemoveExisting = useCallback(() => {
  //   setFiles([])
  //   setUploadedFiles([])
  //   setUploadProgress(0)
  //   onChange([])
  // }, [onChange])

  return {
    fileId: firstFileId || '',
    fileIds: value,
    fileBlob,
    imageIds: value,
    imageUrls: [],
    files,
    uploadedFile: uploadedFiles[0] || null,
    uploadedFiles,
    uploadProgress,
    handleChange,
    handleRemoveExisting,
    isUploading: uploadFile.isPending,
    isLoadingFile,
    isFetchingFile,
    isLoadingFileUrl: false,
    isFetchingFileUrl: false,
    refetchFile,
    error: uploadFile.error,
  }
}
